/**
 * Speed trails: clear -> green -> yellow -> red, for players AND the ball
 * (founder ask, 2026-08-27).
 *
 * Separate from Player Trails and Ball Trail on purpose. Those two answer
 * "whose is it" — team colour, possession colour. This one answers "how fast",
 * and a single trail cannot carry both without one of them lying. Run them
 * together if you want; they are independent toggles.
 *
 * Both entities use the same ramp and different scales: see speedRamp.ts. The
 * player side reuses buildTrails from playerTrails.ts rather than regrouping
 * the buffer a second way.
 */
import { buildTrails } from './playerTrails'
import { BALL_SPEED, fraction, rampColor } from './speedRamp'
import type { TrackingFrame } from './trackingLoader'

/** 5s at 30fps, matching the plain player trails. */
export const TRAIL_FRAMES = 150

interface SpeedTrailsOptions {
  getCanvasState: () => {
    ctx: CanvasRenderingContext2D | null
    frames: TrackingFrame[]
    currentFrameIndex: number
    highlightedPlayers: string[]
  }
  convertToCanvasCoords: (x: number, y: number) => { x: number; y: number }
}

export class SpeedTrailsManager {
  enabled = false
  config = {
    trailFrames: TRAIL_FRAMES,
    /** Width at the bottom and top of the ramp. */
    minWidth: 1.25,
    maxWidth: 5,
    /** The ball is one object, not 22 — it can afford to be drawn heavier. */
    ballWidthScale: 1.3,
    /** Speed a player has no reading for: mid-ramp, so a feed without a Speed
     *  column still draws a trail rather than an invisible one. */
    unknown: 0.45,
  }

  private opts: SpeedTrailsOptions

  constructor(opts: SpeedTrailsOptions) {
    this.opts = opts
  }

  draw(): void {
    if (!this.enabled) return
    const { ctx, frames, currentFrameIndex, highlightedPlayers } = this.opts.getCanvasState()
    if (!ctx || !frames.length) return

    ctx.save()
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    // ---- players ----
    const trails = buildTrails(frames, currentFrameIndex, {
      trailFrames: this.config.trailFrames,
      only: highlightedPlayers,
    })
    for (const trail of trails) {
      for (let i = 1; i < trail.points.length; i++) {
        const p0 = trail.points[i - 1]
        const p1 = trail.points[i]
        if (p0 === undefined || p1 === undefined) continue
        const a = this.opts.convertToCanvasCoords(p0.x, p0.y)
        const b = this.opts.convertToCanvasCoords(p1.x, p1.y)
        this.stroke(ctx, a, b, p1.s ?? this.config.unknown, p1.t, 1)
      }
    }

    // ---- ball ----
    const end = Math.min(currentFrameIndex, frames.length - 1)
    const start = Math.max(0, end - this.config.trailFrames + 1)
    const span = Math.max(1, end - start)
    let prev: { x: number; y: number } | null = null
    for (let i = start; i <= end; i++) {
      const ball = frames[i]?.ball
      if (!ball || !Number.isFinite(ball.x) || !Number.isFinite(ball.y)) {
        // A detection gap breaks the line — the ball did not fly that chord.
        prev = null
        continue
      }
      const pt = this.opts.convertToCanvasCoords(ball.x, ball.y)
      if (prev) {
        const s = fraction(ball.speed, BALL_SPEED) ?? this.config.unknown
        this.stroke(ctx, prev, pt, s, (i - start) / span, this.config.ballWidthScale)
      }
      prev = pt
    }

    ctx.restore()
  }

  /** One segment: colour and width from speed, extra fade from age. */
  private stroke(
    ctx: CanvasRenderingContext2D,
    a: { x: number; y: number },
    b: { x: number; y: number },
    speed: number,
    age: number,
    widthScale: number,
  ): void {
    const w = this.config.minWidth + (this.config.maxWidth - this.config.minWidth) * speed
    ctx.lineWidth = w * widthScale
    // The ramp's own alpha carries "clear at a standstill"; age only dims what
    // is already there, and never to nothing, or the tail would vanish.
    ctx.globalAlpha = 0.35 + 0.65 * age
    ctx.strokeStyle = rampColor(speed)
    ctx.beginPath()
    ctx.moveTo(a.x, a.y)
    ctx.lineTo(b.x, b.y)
    ctx.stroke()
  }
}
